/**
 * Canonical HKDSE subject codes (no underscore compound codes).
 * Short display labels live in i18n under `dseSubjectAliases`.
 * Full names live under `dseSubjects`.
 *
 * BAF can be entered directly or derived as max(BAF, ACC, BUS) for formulae and
 * programme requirements. MEP and TNL are virtual-only: never shown in the UI as
 * standalone codes; display resolves to their member papers (M1/M2, FST/FCT).
 */

export const DSE_CORE_SUBJECT_CODES = Object.freeze(['CHI', 'ENG', 'MAT', 'CSD']);

export const DSE_ELECTIVE_SELECTABLE_SUBJECT_CODES = Object.freeze([
  'BIO', 'CHE', 'PHY', 'CHL', 'LIE', 'CHH', 'ECO', 'ERS', 'GEO', 'HIS', 'THS',
  'BAF', 'ACC', 'BUS', 'DAT', 'HSC', 'ICT', 'FST', 'FCT',
  'MUS', 'VSA', 'PHE', 'ME1', 'ME2',
]);

export const DSE_SELECTABLE_SUBJECT_CODES = Object.freeze([
  ...DSE_CORE_SUBJECT_CODES,
  ...DSE_ELECTIVE_SELECTABLE_SUBJECT_CODES,
]);

export const VIRTUAL_SUBJECT_MEMBERS = Object.freeze({
  BAF: Object.freeze(['BAF', 'ACC', 'BUS']),
  TNL: Object.freeze(['FST', 'FCT']),
  MEP: Object.freeze(['ME1', 'ME2']),
});

/** Virtual codes used in programme data but never shown directly in the UI. */
export const DSE_VIRTUAL_ONLY_SUBJECT_CODES = Object.freeze(['MEP', 'TNL']);

export const DSE_VIRTUAL_DERIVABLE_SUBJECT_CODES = Object.freeze(['BAF']);

export const DSE_VIRTUAL_SUBJECT_CODES = Object.freeze([
  ...DSE_VIRTUAL_DERIVABLE_SUBJECT_CODES,
  ...DSE_VIRTUAL_ONLY_SUBJECT_CODES,
]);

export const DSE_SUBJECT_CODES = Object.freeze([
  ...DSE_SELECTABLE_SUBJECT_CODES,
  ...DSE_VIRTUAL_SUBJECT_CODES,
]);

export const DSE_SUBJECT_CODE_SET = new Set(DSE_SELECTABLE_SUBJECT_CODES);

/** Legacy stored / UI codes → canonical codes. */
export const LEGACY_SUBJECT_CODE_ALIASES = Object.freeze({
  BAF_ACC: 'ACC',
  BAF_BUS: 'BUS',
  TNL_FST: 'FST',
  TNL_FCT: 'FCT',
  MEP_M1: 'ME1',
  MEP_M2: 'ME2',
});

export function normalizeSubjectCode(code) {
  if (!code) return code;
  return LEGACY_SUBJECT_CODE_ALIASES[code] || code;
}

export function isVirtualSubjectCode(code) {
  const canonical = normalizeSubjectCode(code);
  return canonical in VIRTUAL_SUBJECT_MEMBERS;
}

export function isVirtualOnlySubjectCode(code) {
  const canonical = normalizeSubjectCode(code);
  return DSE_VIRTUAL_ONLY_SUBJECT_CODES.includes(canonical);
}

export function getVirtualSubjectMembers(code) {
  const canonical = normalizeSubjectCode(code);
  return VIRTUAL_SUBJECT_MEMBERS[canonical] || null;
}

function getVirtualOnlySubjectShortLabel(t, code) {
  const members = getVirtualSubjectMembers(code);
  if (!members?.length) return code;
  return members
    .map((member) => t(`dseSubjectAliases.${member}`, { defaultValue: member }))
    .join('/');
}

function getVirtualOnlySubjectFullName(t, code) {
  const members = getVirtualSubjectMembers(code);
  if (!members?.length) return code;
  return members
    .map((member) => t(`dseSubjects.${member}`, { defaultValue: member }))
    .join(' / ');
}

export function getSubjectShortLabel(t, code) {
  const canonical = normalizeSubjectCode(code);
  if (isVirtualOnlySubjectCode(canonical)) {
    return getVirtualOnlySubjectShortLabel(t, canonical);
  }
  return t(`dseSubjectAliases.${canonical}`, { defaultValue: canonical });
}

export function getSubjectFullName(t, code) {
  const canonical = normalizeSubjectCode(code);
  if (isVirtualOnlySubjectCode(canonical)) {
    return getVirtualOnlySubjectFullName(t, canonical);
  }
  return t(`dseSubjects.${canonical}`, { defaultValue: canonical });
}
